import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay, unitLabel, zonedDay } from '@d-orca/shared/client/logistics';
import type { WasteEntry } from '@interfaces/ILogistics';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { WasteChart, type WasteChartView } from './WasteChart';
import type { WasteOutlook } from './wasteChart.logic';
import { wasteAmount, wasteNumber } from './wasteFormat';
import styles from './WasteDetail.module.css';

interface IWasteDetailProps {
  row: WasteEntry;
  tz: string;
}

/** A "—" where a cycle predates the leftover count: the batch is unknown,
 *  and a blank would read as zero. */
function figure(t: (key: string) => string, value: number | null, unit: string): string {
  return value == null ? '—' : wasteAmount(t, value, unit);
}

/**
 * The expanded Waste row: three tiles (the per-cycle average and the two
 * projections), the chart with its Per cycle / Over time switch, and the
 * cycle table — always present, so the chart is never the only reading.
 */
export function WasteDetail({ row, tz }: IWasteDetailProps) {
  const { t, i18n } = useTranslation();
  // The page's short M/D day on the site's calendar, like the board and inventory.
  const day = (iso: string) => formatDay(zonedDay(iso, tz), i18n.language);
  const [view, setView] = useState<WasteChartView>('perCycle');
  const [outlook, setOutlook] = useState<WasteOutlook>('year');
  const { history, projection } = row;
  // The stored unit is a key (or a legacy label); every line reads it in the
  // viewer's language.
  const unit = unitLabel(t, row.unit);
  const views = [
    { id: 'perCycle' as const, label: t('logistics.waste.viewPerCycle') },
    { id: 'overTime' as const, label: t('logistics.waste.viewOverTime') },
  ];
  const outlooks = [
    { id: 'year' as const, label: t('logistics.waste.outlookYear') },
    { id: 'nextCycle' as const, label: t('logistics.waste.outlookNextCycle') },
  ];
  // The average is an observed figure; the two horizons are projections and
  // wear "~" and the planned blue for it. No projection: dashes, no units.
  const tiles = [
    {
      label: t('logistics.waste.tilePerCycle'),
      value: projection ? String(wasteNumber(projection.perCycle, row.unit)) : '—',
      unit: projection ? t('logistics.waste.tileAvgUnit', { unit }) : null,
      projected: false,
    },
    {
      label: t('logistics.waste.tileIn6Months'),
      value: projection ? `~${wasteNumber(projection.in6Months, row.unit)}` : '—',
      unit: projection ? unit : null,
      projected: true,
    },
    {
      label: t('logistics.waste.tileIn12Months'),
      value: projection ? `~${wasteNumber(projection.in12Months, row.unit)}` : '—',
      unit: projection ? unit : null,
      projected: true,
    },
  ];

  return (
    <div className={styles.detail}>
      <div className={styles.tiles}>
        {tiles.map((tile) => (
          <div key={tile.label} className={tile.projected ? `${styles.tile} ${styles.tileProjected}` : styles.tile}>
            <span className={styles.tileLabel}>{tile.label}</span>
            <span className={styles.tileValue}>
              {tile.value}
              {/* A real space, like "4.1 t" everywhere else — not a margin
                  that copies and reads as "4.1t". */}
              {tile.unit ? <> <small>{tile.unit}</small></> : null}
            </span>
          </div>
        ))}
      </div>
      {/* The projection is a rule, stated; until a cycle has closed with
          waste there is no rate to state, and the tiles say so rather than
          show a dash alone. */}
      <p className={styles.projectionNote}>
        {projection ? t('logistics.waste.projectionHow') : t('logistics.waste.projectionNotYet')}
      </p>

      <div className={styles.chartBar}>
        <span className={styles.chartName}>
          {view === 'perCycle' ? t('logistics.waste.chartPerCycle', { unit }) : t('logistics.waste.chartOverTime', { unit })}
        </span>
        <SegmentedControl value={view} onChange={setView} options={views} ariaLabel={t('logistics.waste.viewSwitch')} />
      </div>
      {/* Over time's range — only when there is a projection to range over;
          without one both would draw the same bare history. */}
      {view === 'overTime' && projection ? (
        <div className={styles.outlookBar}>
          <SegmentedControl
            value={outlook}
            onChange={setOutlook}
            options={outlooks}
            ariaLabel={t('logistics.waste.outlookSwitch')}
            variant="joined"
          />
        </div>
      ) : null}
      <WasteChart view={view} outlook={outlook} history={history} projection={projection} unit={row.unit} tz={tz} />

      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t('logistics.waste.colCycle')}</th>
            <th>{t('logistics.waste.colOpened')}</th>
            <th>{t('logistics.waste.colClosed')}</th>
            <th className={styles.num}>{t('logistics.waste.colStartedWith')}</th>
            <th className={styles.num}>{t('logistics.waste.colLeft')}</th>
            <th className={styles.num}>{t('logistics.waste.colWaste')}</th>
          </tr>
        </thead>
        <tbody>
          {/* Newest first, like the card's rows — the history arrives oldest
              first for the chart's sake. */}
          {[...history].reverse().map((cycle, index) => (
            <tr key={cycle.closedAt}>
              <td>{history.length - index}</td>
              <td>{day(cycle.openedAt)}</td>
              <td>{day(cycle.closedAt)}</td>
              <td className={styles.num}>{figure(t, cycle.startingStock, row.unit)}</td>
              <td className={styles.num}>{figure(t, cycle.left, row.unit)}</td>
              <td className={styles.num}>
                {cycle.capped ? <span className={styles.tag}>{t('logistics.waste.tagCapped')}</span> : null}
                {!cycle.estimated && cycle.startingStock != null ? (
                  <span className={`${styles.tag} ${styles.tagEdited}`}>{t('logistics.waste.tagEdited')}</span>
                ) : null}
                {wasteAmount(t, cycle.waste, row.unit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
